import type { PlanKey } from "@giftforward/entitlements";
import type { PlanUpsellContext } from "@/interfaces/CampaignBuilder";

const leftOutPhrase = (leftOut: number) =>
  `${leftOut} contact${leftOut === 1 ? " was" : "s were"} left out`;

/**
 * The recipient-cap modal copy for the caller's plan. Starter is told to upgrade to Pro; any
 * other plan is told to upgrade for unlimited, since "Pro" would be wrong for a Pro caller.
 */
export function recipientCapCopy(planKey: PlanKey, cap: number) {
  if (planKey === "starter") {
    return {
      blocked: {
        title: "Contact limit reached",
        description: `Your Starter plan includes up to ${cap} recipients per campaign. Upgrade to Pro to send a single campaign to as many as 2,000 recipients — or keep this one at ${cap} and create a separate campaign for the rest.`,
        primaryLabel: "Upgrade to Pro",
        secondaryLabel: "Maybe Later",
      },
      warning: {
        title: `Only ${cap} Contacts Added!`,
        description: (leftOut: number) =>
          `Your plan allows ${cap} recipients per campaign, so ${leftOutPhrase(leftOut)}. To reach everyone, upgrade to Pro for up to 2,000 recipients per campaign — or create a separate campaign for the rest.`,
        primaryLabel: "Upgrade to Pro",
        secondaryLabel: "Got it",
      },
    };
  }
  return {
    blocked: {
      title: "Contact limit reached",
      description: `Your Pro plan includes up to ${cap} recipients per campaign. Upgrade to have unlimited recipients per campaign.`,
      primaryLabel: "Upgrade",
      secondaryLabel: "Maybe Later",
    },
    warning: {
      title: `Only ${cap} Contacts Added!`,
      description: (leftOut: number) =>
        `Your Pro plan includes up to ${cap} recipients per campaign, so ${leftOutPhrase(leftOut)}. Upgrade to have unlimited recipients per campaign — or create a separate campaign for the rest.`,
      primaryLabel: "Upgrade",
      secondaryLabel: "Got it",
    },
  };
}

/** What the builder shows until the entitlements (plan + cap) have loaded: today's Starter copy. */
const STARTER_RECIPIENT_CAP_COPY = recipientCapCopy("starter", 20);

export const CONFIGS = {
  international: {
    title: "Want to send gifts outside the USA?",
    description: "Upgrade to Pro for access to International gift delivery",
    primaryLabel: "Upgrade",
    secondaryLabel: "Maybe Later",
  },
  edit: {
    title: "Unlock Collection Editing",
    description:
      "Customize your collection — add, remove, and reorder gifts so your recipients see exactly the selection you want.",
    primaryLabel: "Upgrade",
    secondaryLabel: "Maybe Later",
  },
  addMoreGifts: {
    title: "Confirm Adding Gifts",
    primaryLabel: "Confirm & Add",
    secondaryLabel: "Cancel",
  },
  unsavedChanges: {
    title: "Unsaved Changes",
    description:
      "You have unsaved changes to your collection. Would you like to save them before leaving?",
    primaryLabel: "Save Changes",
    secondaryLabel: "Discard",
  },
  recipientCapWarning: STARTER_RECIPIENT_CAP_COPY.warning,
  recipientCapBlocked: STARTER_RECIPIENT_CAP_COPY.blocked,
  schedulePerRecipient: {
    title: "Schedule individual send dates",
    description:
      "Upgrade to Pro to send each recipient their gift on its own date — perfect for birthdays, work anniversaries, and staggered sends. Available on Pro and Enterprise.",
    primaryLabel: "Upgrade to Pro",
    secondaryLabel: "Maybe Later",
  },
  giftReminders: {
    title: "Unlock Gift Reminders",
    description:
      "Send automatic reminder emails to recipients who haven't redeemed their gift yet.",
    primaryLabel: "Upgrade to Pro",
    secondaryLabel: "Maybe Later",
  },
  manualLinks: {
    title: "Unlock Manual Gift Links",
    description:
      "Upgrade to Pro to distribute gift links yourself — download a CSV of unique links and share them through your own channels instead of having us email recipients. Available on Pro and Enterprise.",
    primaryLabel: "Upgrade to Pro",
    secondaryLabel: "Maybe Later",
  },
  universalLinks: {
    title: "Unlock universal links",
    description:
      "Upgrade to Pro to generate a single public link you can share anywhere — in a newsletter, on event signage, or as a QR code — and let anyone redeem a gift until your limit is reached. Available on Pro and Enterprise.",
    primaryLabel: "Upgrade to Pro",
    secondaryLabel: "Maybe Later",
  },
  payg: {
    title: "Unlock Pay-as-you-give billing",
    description:
      "Upgrade to Pro to launch with a 40% deposit instead of paying the full gift budget up front. As recipients claim their gifts, small automatic top-ups keep your spend in step with redemptions — so you fund what’s claimed plus a small buffer, not the full amount on day one. Available on Pro and Enterprise.",
    primaryLabel: "Upgrade to Pro",
    secondaryLabel: "Maybe Later",
  },
  landingBrandColors: {
    title: "Unlock Brand Colors",
    description: "Customize your landing page accent and background colors to match your brand.",
    primaryLabel: "Upgrade to Pro",
    secondaryLabel: "Maybe Later",
  },
  landingConfetti: {
    title: "Unlock Confetti Customization",
    description:
      "Choose custom confetti shapes and color schemes for your gift redemption celebration.",
    primaryLabel: "Upgrade to Pro",
    secondaryLabel: "Maybe Later",
  },
  reminderCustomDates: {
    title: "Customize Your Reminder Schedule",
    description:
      "Upgrade to Pro to set your own date and time for each gift reminder email, in the campaign builder and here in My Campaigns.",
    primaryLabel: "Upgrade to Pro",
    secondaryLabel: "Maybe Later",
  },
  // A plain confirm dialog — description is composed dynamically (the X-day count) in the modal's
  // CONFIGS, so only the labels live here.
  shortExpiryWarning: {
    title: "That's a short window",
    primaryLabel: "Yes, keep it short",
    secondaryLabel: "Cancel",
  },
  // Manual gift links — informational ack (single button). The description is built dynamically in
  // CampaignBuilderModal from the stranded tag list, so only the title/label live here.
  strandedTags: {
    title: "Some tags won't work with gift links",
    primaryLabel: "Got it",
  },
  // GFG-443 — a CSV with send dates on a campaign not set to "Send on different dates". The
  // description names the current schedule, so it is built in CampaignBuilderModal.
  sendDatesConflict: {
    title: "Your file has send dates",
    primaryLabel: "Use the file's dates",
    secondaryLabel: "Ignore the dates",
  },
  // GFG-443 — informational ack; the description lists the defaults applied.
  sendDefaultsApplied: {
    title: "Check your send dates",
    primaryLabel: "Got it",
  },
} as const;

/**
 * The plan modal's pill title. The cap contexts follow the caller's plan; with no plan or cap
 * loaded yet they keep the Starter copy, like the CONFIGS entries.
 */
export function planModalContextTitle(
  context: PlanUpsellContext,
  planKey: PlanKey | null,
  cap: number | null,
): string {
  if (planKey !== null && cap !== null) {
    if (context === "recipientCapWarning") return recipientCapCopy(planKey, cap).warning.title;
    if (context === "recipientCapBlocked") return recipientCapCopy(planKey, cap).blocked.title;
  }
  return CONFIGS[context].title;
}
